import { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

export const STATUS = {
  PENDING: 'PENDING',
  READY: 'READY',
};

export function usePageParams(config) {
  const [searchParams, setSearchParams] = useSearchParams();

  const { status, params } = useMemo(() => {
    const result = {};
    let missingRequired = false;

    for (const { key, required, defaultValue } of config) {
      const fromUrl = searchParams.get(key);

      if (fromUrl != null) {
        // value is set and ready
        result[key] = fromUrl;
        continue;
      }

      if (typeof defaultValue !== 'undefined') {
        // use defaultValue as fallback
        const v = typeof defaultValue === 'function' ? defaultValue() : defaultValue;
        result[key] = v;
        continue;
      }

      if (required) {
        // required
        missingRequired = true;
        result[key] = null;
      } else {
        // optional
        result[key] = null;
      }
    }

    const status = missingRequired ? STATUS.PENDING : STATUS.READY;
    return { status, params: result };
  }, [searchParams, config]);

  // sync selected params (with sync: true) into the URL when they are missing
  useEffect(() => {
    if (status !== STATUS.READY) return;

    const next = new URLSearchParams(searchParams);
    let changed = false;

    for (const { key, sync } of config) {
      if (!sync) continue; // only sync the opted in keys

      const hasInUrl = searchParams.has(key);
      const value = params[key];

      if (!hasInUrl && value !== null) {
        next.set(key, value);
        changed = true;
      }
    }

    if (changed) {
      setSearchParams(next, { replace: true });
    }
  }, [status, params, config, searchParams, setSearchParams]);

  const updateParams = (updates, options = {}) => {
    const { replace = true } = options;

    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const [key, value] of Object.entries(updates)) {
          if (value === null) {
            next.delete(key);
          } else {
            next.set(key, value);
          }
        }
        return next;
      },
      { replace },
    );
  };

  return {
    status,
    params,
    setParams: updateParams,
  };
}
